import { Scale, Building2, Gavel, Landmark, Users, Heart, Handshake } from 'lucide-react';
import { motion } from 'framer-motion';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.5 },
};

export default function PracticeAreasPage() {
  const practiceAreas = [
    {
      title: '형사 사건',
      icon: Gavel,
      description: '전문적인 형사 변호를 통한 고객의 권리 보호',
      details: [
        '경범죄부터 중대범죄까지 전문 변호',
        '수사단계부터 재판까지 종합적 대응',
        '보석 및 구속영장 관련 법률 자문',
        '범죄피해자 고소대리',
      ],
      color: '#b98e6a',
      bgColor: '#f5efe8',
    },
    {
      title: '금융∙기업 자문 및 송무',
      icon: Landmark,
      description: '금융∙기업 전담팀의 원스톱 지원 서비스',
      details: [
        '금융기관, 기업 법률 자문',
        '금융∙기업 관련 분쟁 해결',
        '금융규제 대응',
        '투자 관련 법률 자문',
      ],
      color: '#b98e6a',
      bgColor: '#f5efe8',
    },
    {
      title: '기업금융 및 부동산 PF',
      icon: Building2,
      description: '기업금융, 부동산 PF 종합 서비스',
      details: [
        '부동산 개발 프로젝트 종합 자문',
        '부동산 투자 관련 법률 자문',
        '자산유동화(실물투자, 대체투자)',
        '사모집합투자기구 설립 및 운영',
      ],
      color: '#b98e6a',
      bgColor: '#f5efe8',
    },
    {
      title: '경영권 분쟁',
      icon: Scale,
      description: '기업의 경영권 관련 분쟁 해결 및 자문',
      details: [
        '주주총회 관련 분쟁',
        'M&A 관련 법률 자문',
        '기업지배구조 관련 자문',
        '직무집행정지 가처분',
      ],
      color: '#b98e6a',
      bgColor: '#f5efe8',
    },
    {
      title: '가사 분쟁',
      icon: Heart,
      description: '상속, 이혼 등 가족 관련 원스톱 법률 서비스',
      details: ['이혼 및 재산분할', '상속 및 유산 분쟁', '상속세 및 증여세 관련 원스톱 컨설팅', '친권 및 양육권 관련 분쟁'],
      color: '#b98e6a',
      bgColor: '#f5efe8',
    },
    {
      title: '민사 사건',
      icon: Handshake,
      description: '민사 전반의 다양한 법률 분쟁 해결',
      details: ['계약 관련 분쟁/ 손해배상청구', '임대차, 매매, 근로, 투자 등 관련 분쟁 해결', '건설∙부동산, 재개발∙재건축 자문 및 소송', '보전처분(가압류, 가처분) 및 민사집행'],
      color: '#b98e6a',
      bgColor: '#f5efe8',
    },
  ];

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen bg-gradient-to-b from-background to-background/80"
    >
      {/* 헤더 섹션 */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="pt-32 pb-20 bg-white relative overflow-hidden"
      >
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[#fdfaf7]"></div>
          <div className="absolute inset-0 bg-[url('/images/lawyers.jpg')] opacity-5"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-block mb-6"
            >
              <span className="px-4 py-2 bg-[#b98e6a]/10 text-[#b98e6a] rounded-full text-sm font-medium">
                전문 법률 서비스
              </span>
            </motion.div>
            <motion.h1
              className="text-5xl md:text-6xl font-bold text-[#8b6b4f] mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              업무 분야
            </motion.h1>
            <motion.p
              className="text-xl text-gray-600 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              법무법인 휘상은 다양한 법률 분야에서 전문적인 서비스를 제공합니다.
              <br />각 분야별 전문 변호사들이 최선을 다해 고객의 권리를 지키겠습니다.
            </motion.p>
          </div>
        </div>
      </motion.section>

      {/* 업무 분야 그리드 */}
      <section className="py-16 relative">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {practiceAreas.map((area, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                whileHover={{
                  scale: 1.02,
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                }}
                className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 relative"
              >
                <div
                  className={`absolute inset-0 bg-[${area.bgColor}] opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                />
                <div className="p-8 relative">
                  <div
                    className={`w-16 h-16 bg-[${area.color}] rounded-xl flex items-center justify-center mb-6 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 shadow-lg`}
                    style={{ backgroundColor: area.color }}
                  >
                    <area.icon className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-[#b98e6a] transition-colors duration-300">
                    {area.title}
                  </h2>
                  <p className="text-gray-600 mb-6 group-hover:text-gray-900 transition-colors duration-300">
                    {area.description}
                  </p>
                  <ul className="space-y-3">
                    {area.details.map((detail, idx) => (
                      <li
                        key={idx}
                        className="flex items-center text-gray-600 group-hover:text-gray-900 transition-colors duration-300"
                      >
                        <span
                          className="w-2 h-2 bg-black rounded-full mr-3 group-hover:scale-150 transition-transform duration-300"
                        ></span>
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 문구 섹션 */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="py-16 bg-white relative"
      >
        <div className="absolute inset-0 bg-[#f5efe8]/50" />
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="text-center">
            <motion.h2
              className="text-4xl md:text-5xl font-bold text-[#8b6b4f] mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              전문성과 신뢰성
            </motion.h2>
            <motion.p
              className="text-xl text-gray-600 max-w-3xl mx-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              각 분야별 전문 변호사들이 함께하여
              <br />
              최고의 법률 서비스를 제공합니다
            </motion.p>
          </div>
        </div>
      </motion.section>
    </motion.div>
  );
}
